/**
 * PlatformCadenceEditor — per-strategy, per-platform cadence form.
 *
 * Edits the strategy-specific cadence consumed by assignTimeSlots. One
 * section per enabled platform edits exact weekly posting slots.
 * postsPerWeek is derived from slot count.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Menu, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { PlatformId, StrategyPlatformCadence } from '../../types';
import { PlatformCadenceFields } from '@modules/content-plan/components/platforms/PlatformCadenceFields';
import {
  cadenceToFieldSlots,
  defaultStrategyCadenceForPlatform,
  postsPerWeekFromCadence,
  withFieldSlots,
} from '@modules/content-plan/utils/strategyCadence';

export interface PlatformCadenceEditorProps {
  open: boolean;
  onClose: () => void;
  initialCadences: StrategyPlatformCadence[];
  platformOptions: PlatformId[];
  timeZone?: string;
  onSave: (next: StrategyPlatformCadence[]) => Promise<void>;
}

function defaultCadenceFor(platform: PlatformId): StrategyPlatformCadence {
  return defaultStrategyCadenceForPlatform({
    platform,
    postsPerWeek: 2,
  });
}

export const PlatformCadenceEditor: React.FC<PlatformCadenceEditorProps> = ({
  open, onClose, initialCadences, platformOptions, timeZone, onSave,
}) => {
  const [cadences, setCadences] = useState<StrategyPlatformCadence[]>(initialCadences);
  const [expandedPlatforms, setExpandedPlatforms] = useState<PlatformId[]>(
    initialCadences[0]?.platform ? [initialCadences[0].platform] : [],
  );
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setCadences(initialCadences);
    setExpandedPlatforms(initialCadences[0]?.platform ? [initialCadences[0].platform] : []);
  }, [open, initialCadences]);

  const availablePlatforms = useMemo(
    () => platformOptions.filter((platform) => !cadences.some((cadence) => cadence.platform === platform)),
    [cadences, platformOptions],
  );

  const updateCadence = (p: PlatformId, patch: Partial<StrategyPlatformCadence>) => {
    setCadences((prev) => {
      const exists = prev.some((c) => c.platform === p);
      if (!exists) return [...prev, { ...defaultCadenceFor(p), ...patch }];
      return prev.map((c) => (c.platform === p ? { ...c, ...patch } : c));
    });
  };

  const addPlatform = (platform: PlatformId) => {
    setCadences((prev) => (prev.some((cadence) => cadence.platform === platform)
      ? prev
      : [...prev, defaultCadenceFor(platform)]));
    setExpandedPlatforms((current) => current.includes(platform) ? current : [...current, platform]);
    setAddMenuAnchor(null);
  };

  const removePlatform = (platform: PlatformId) => {
    setCadences((prev) => prev.filter((cadence) => cadence.platform !== platform));
    setExpandedPlatforms((current) => current.filter((candidate) => candidate !== platform));
  };

  const togglePlatformExpanded = (platform: PlatformId) => {
    setExpandedPlatforms((current) =>
      current.includes(platform)
        ? current.filter((candidate) => candidate !== platform)
        : [...current, platform],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Normalize timezone into every cadence if the dialog has a global TZ.
      const normalized = cadences.map((c) => ({
        ...c,
        postsPerWeek: postsPerWeekFromCadence(c),
        timeZone: c.timeZone ?? timeZone,
      }));
      await onSave(normalized);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 24px)', md: 900 },
          maxWidth: 'calc(100vw - 24px)',
          height: { xs: 'calc(100vh - 24px)', md: 'min(760px, calc(100vh - 48px))' },
          maxHeight: 'calc(100vh - 24px)',
        },
      }}
      data-testid={CONTENT_PLAN.CADENCE_EDITOR_DIALOG}
    >
      <DialogTitle>Platform cadence</DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>
        <Typography variant="caption" color="text.secondary">
          Controls when auto-scheduled posts land on each platform. Weekly volume is the number of slots.
        </Typography>
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={availablePlatforms.length === 0}
            onClick={(event) => setAddMenuAnchor(event.currentTarget)}
            data-testid={CONTENT_PLAN.CADENCE_ADD_PLATFORM}
          >
            Add platform
          </Button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={() => setAddMenuAnchor(null)}
          >
            {availablePlatforms.map((platform) => (
              <MenuItem
                key={platform}
                onClick={() => addPlatform(platform)}
                data-testid={CONTENT_PLAN.CADENCE_ADD_PLATFORM_OPTION}
                data-platform={platform}
              >
                {String(platform).charAt(0).toUpperCase() + String(platform).slice(1)}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {cadences.length === 0 && (
            <Alert severity="info">No platform cadences configured.</Alert>
          )}
          {cadences.map((c) => {
            const expanded = expandedPlatforms.includes(c.platform);
            return (
              <Paper
                key={c.platform}
                variant="outlined"
                data-testid={CONTENT_PLAN.CADENCE_PLATFORM_SECTION}
                data-platform={c.platform}
                sx={{ overflow: 'hidden' }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ p: 1.5, cursor: 'pointer' }}
                  onClick={() => togglePlatformExpanded(c.platform)}
                >
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', flex: 1 }}>
                    {c.platform}
                  </Typography>
                  <Chip size="small" variant="outlined" label={`${postsPerWeekFromCadence(c)} / week`} />
                  <IconButton
                    size="small"
                    aria-label="Remove platform cadence"
                    data-testid={CONTENT_PLAN.CADENCE_PLATFORM_REMOVE}
                    onClick={(event) => {
                      event.stopPropagation();
                      removePlatform(c.platform);
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={expanded ? 'Collapse platform cadence' : 'Expand platform cadence'}
                    data-testid={CONTENT_PLAN.CADENCE_PLATFORM_TOGGLE}
                    onClick={(event) => {
                      event.stopPropagation();
                      togglePlatformExpanded(c.platform);
                    }}
                  >
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Stack>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <Stack spacing={1.5} sx={{ p: 1.5, pt: 0 }}>
                    <PlatformCadenceFields
                      mode="strategy"
                      slots={cadenceToFieldSlots(c)}
                      onSlotsChange={(slots) => {
                        const next = withFieldSlots(c, slots);
                        updateCadence(c.platform, next);
                      }}
                      testIdPrefix={`platform-cadence-${c.platform}`}
                    />
                    <TextField
                      data-testid={CONTENT_PLAN.CADENCE_TIMEZONE}
                      size="small"
                      label="Time zone (optional IANA)"
                      placeholder={timeZone ?? 'UTC'}
                      value={c.timeZone ?? ''}
                      onChange={(e) => updateCadence(c.platform, { timeZone: e.target.value || undefined })}
                    />
                  </Stack>
                </Collapse>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid={CONTENT_PLAN.CADENCE_CANCEL} onClick={onClose}>Cancel</Button>
        <Button
          data-testid={CONTENT_PLAN.CADENCE_SAVE}
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
